/**
 * Service for project contribution tracking API interactions.
 *
 * Handles all API calls for projects, contributions, and reference data.
 */

import { apiService } from '../../../services/api';
import {
  Project,
  Contribution,
  ReferenceData,
  TeamMember,
  ProjectSummary,
  UserProjectSummary,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateContributionRequest,
  UpdateContributionRequest,
  TransferOwnershipRequest,
  ProjectWithETag,
  ConcurrencyError,
  ContributionSearchRequest,
  ContributionSearchResult,
} from '../types';

class ProjectService {
  private baseUrl = '/api/projects';
  private get api() {
    return apiService.getInstance();
  }

  // ==================== PROJECT ENDPOINTS ====================

  /**
   * Create new project.
   */
  async createProject(request: CreateProjectRequest): Promise<{ id: string; message: string }> {
    const response = await this.api.post(this.baseUrl, request);
    return response.data;
  }

  /**
   * List projects (my created or all if admin).
   */
  async listProjects(): Promise<ProjectSummary[]> {
    const response = await this.api.get<ProjectSummary[]>(this.baseUrl);
    return response.data;
  }

  /**
   * List projects where user is assigned as team member (read-only view).
   */
  async listMyProjects(): Promise<UserProjectSummary[]> {
    const response = await this.api.get<UserProjectSummary[]>(`${this.baseUrl}/my`);
    return response.data;
  }

  /**
   * Get project details.
   */
  async getProject(projectId: string): Promise<Project> {
    const response = await this.api.get<Project>(`${this.baseUrl}/${projectId}`);
    return response.data;
  }

  /**
   * Get project with ETag for optimistic locking.
   */
  async getProjectWithETag(projectId: string): Promise<ProjectWithETag> {
    const response = await this.api.get<Project>(`${this.baseUrl}/${projectId}`);
    const etag = response.headers['etag'] || '';
    return {
      project: response.data,
      etag,
    };
  }

  /**
   * Update project details.
   * Supports optimistic concurrency control via ETag.
   */
  async updateProject(
    projectId: string,
    request: UpdateProjectRequest,
    etag?: string
  ): Promise<void> {
    try {
      const headers: Record<string, string> = {};
      if (etag) {
        headers['If-Match'] = etag;
      }

      await this.api.put(`${this.baseUrl}/${projectId}`, request, { headers });
    } catch (error: any) {
      if (error.response?.status === 412) {
        // Precondition Failed - concurrent modification
        const concurrencyError = new Error(
          error.response.data?.detail?.message ||
            'This project was modified by another user. Please refresh and try again.'
        ) as ConcurrencyError;
        concurrencyError.name = 'ConcurrencyError';
        concurrencyError.action = 'refresh';
        throw concurrencyError;
      }
      throw error;
    }
  }

  /**
   * Delete project and all associated contributions (creator or admin only).
   */
  async deleteProject(projectId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${projectId}`);
  }

  /**
   * Transfer project ownership (admin only).
   */
  async transferOwnership(projectId: string, request: TransferOwnershipRequest): Promise<void> {
    await this.api.put(`${this.baseUrl}/${projectId}/creator`, request);
  }

  // ==================== TEAM MANAGEMENT ====================

  /**
   * Add team member to project.
   */
  async addTeamMember(projectId: string, member: TeamMember): Promise<void> {
    await this.api.post(`${this.baseUrl}/${projectId}/team`, member);
  }

  /**
   * Remove team member from project.
   */
  async removeTeamMember(projectId: string, userId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${projectId}/team/${userId}`);
  }

  /**
   * Update team member details (role, contribution, etc.).
   */
  async updateTeamMember(projectId: string, userId: string, updates: Partial<TeamMember>): Promise<void> {
    await this.api.patch(`${this.baseUrl}/${projectId}/team/${userId}`, updates);
  }

  // ==================== CONTRIBUTION ENDPOINTS ====================

  /**
   * Log contribution for any team member.
   */
  async createContribution(request: CreateContributionRequest): Promise<{ id: string; message: string }> {
    const response = await this.api.post(`${this.baseUrl}/contributions`, request);
    return response.data;
  }

  /**
   * Get all contributions for a project.
   */
  async getProjectContributions(projectId: string): Promise<Contribution[]> {
    const response = await this.api.get<Contribution[]>(
      `${this.baseUrl}/${projectId}/contributions`
    );
    return response.data;
  }

  /**
   * Get all contributions logged for current user across all projects.
   */
  async getMyContributions(): Promise<Contribution[]> {
    const response = await this.api.get<Contribution[]>(`${this.baseUrl}/contributions/my`);
    return response.data;
  }

  /**
   * Update contribution.
   */
  async updateContribution(
    projectId: string,
    contributionId: string,
    request: UpdateContributionRequest
  ): Promise<void> {
    await this.api.put(
      `${this.baseUrl}/contributions/${projectId}/${contributionId}`,
      request
    );
  }

  /**
   * Delete contribution.
   */
  async deleteContribution(projectId: string, contributionId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/contributions/${projectId}/${contributionId}`);
  }

  /**
   * Search contributions across all projects (experience search).
   */
  async searchContributions(request: ContributionSearchRequest): Promise<ContributionSearchResult[]> {
    const response = await this.api.post<ContributionSearchResult[]>(
      `${this.baseUrl}/contributions/search`,
      request
    );
    return response.data;
  }

  // ==================== REFERENCE DATA ====================

  /**
   * Get reference data (taxonomies, roles, etc.).
   */
  async getReferenceData(): Promise<ReferenceData> {
    const response = await this.api.get<ReferenceData>(`${this.baseUrl}/reference-data`);
    return response.data;
  }

  /**
   * Update reference data (admin only).
   */
  async updateReferenceData(data: ReferenceData): Promise<void> {
    try {
      await this.api.post(`${this.baseUrl}/reference-data`, data);
    } catch (error: any) {
      if (error.response?.status === 409) {
        // Conflict - concurrent modification
        const concurrencyError = new Error(
          error.response.data?.detail?.message ||
            'Reference data is being edited by another admin. Please try again in a moment.'
        ) as ConcurrencyError;
        concurrencyError.name = 'ConcurrencyError';
        concurrencyError.action = 'retry';
        throw concurrencyError;
      }
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check if error is a concurrency error.
   */
  isConcurrencyError(error: any): error is ConcurrencyError {
    return error.name === 'ConcurrencyError';
  }

  /**
   * Get indications filtered by therapeutic area.
   */
  getIndicationsForTherapeuticArea(
    referenceData: ReferenceData,
    therapeuticAreaCode: string
  ): ReferenceData['indications'] {
    return referenceData.indications.filter(
      (ind) => ind.therapeuticArea === therapeuticAreaCode
    );
  }

  /**
   * Get therapeutic area by code.
   */
  getTherapeuticAreaByCode(
    referenceData: ReferenceData,
    code: string
  ): ReferenceData['therapeuticAreas'][0] | undefined {
    return referenceData.therapeuticAreas.find((ta) => ta.code === code);
  }

  /**
   * Get indication by code.
   */
  getIndicationByCode(
    referenceData: ReferenceData,
    code: string
  ): ReferenceData['indications'][0] | undefined {
    return referenceData.indications.find((ind) => ind.code === code);
  }

  /**
   * Format date for display.
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Format date for input (YYYY-MM-DD).
   */
  formatDateForInput(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  /**
   * Group contributions by team member.
   */
  groupContributionsByMember(contributions: Contribution[]): Map<string, Contribution[]> {
    const contribsByMember = new Map<string, Contribution[]>();

    contributions.forEach((contrib) => {
      const existing = contribsByMember.get(contrib.teamMemberId) || [];
      existing.push(contrib);
      contribsByMember.set(contrib.teamMemberId, existing);
    });

    return contribsByMember;
  }
}

// Export singleton instance
export const projectService = new ProjectService();
export default projectService;
